import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import {
  validateEmail,
  validatePassword,
  validateName,
} from "../utils/validators";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import { Card } from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Toast from "../components/common/Toast";
import { useTheme } from "../context/ThemeContext";

const SignUp = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    country: "",
    agreeToTerms: false,
    agreeToMarketing: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const countries = [
    "United States",
    "United Kingdom",
    "Canada",
    "Australia",
    "Germany",
    "France",
    "Japan",
    "South Korea",
    "Singapore",
    "Netherlands",
    "Switzerland",
    "Sweden",
    "Norway",
    "Denmark",
    "Finland",

    // East Africa
    "Kenya",
    "Tanzania",
    "Uganda",
    "Rwanda",
    "Burundi",
    "Ethiopia",
    "Somalia",
    "Eritrea",
    "Djibouti",
    "South Sudan",

    // West Africa
    "Nigeria",
    "Ghana",
    "Senegal",
    "Mali",
    "Burkina Faso",
    "Niger",
    "Guinea",
    "Sierra Leone",
    "Liberia",
    "Ivory Coast",
    "Togo",
    "Benin",
    "Gambia",
    "Guinea-Bissau",
    "Cape Verde",
    "Mauritania",

    // Southern Africa
    "South Africa",
    "Zimbabwe",
    "Botswana",
    "Namibia",
    "Zambia",
    "Malawi",
    "Mozambique",
    "Lesotho",
    "Eswatini",
    "Madagascar",
  ];
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (formData.password) {
      const strength = calculatePasswordStrength(formData.password);
      setPasswordStrength(strength);
    }
  }, [formData.password]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 25;
    if (password.match(/[a-z]/)) score += 25;
    if (password.match(/[A-Z]/)) score += 25;
    if (password.match(/[0-9]/)) score += 25;
    if (password.match(/[^a-zA-Z0-9]/)) score += 25;
    return Math.min(score, 100);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return "bg-red-500";
    if (passwordStrength < 50) return "bg-orange-500";
    if (passwordStrength < 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return "Weak";
    if (passwordStrength < 50) return "Fair";
    if (passwordStrength < 75) return "Good";
    return "Strong";
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      // Personal Information
      const firstNameError = validateName(formData.firstName, "First name");
      if (firstNameError) newErrors.firstName = firstNameError;

      const lastNameError = validateName(formData.lastName, "Last name");
      if (lastNameError) newErrors.lastName = lastNameError;

      const emailError = validateEmail(formData.email);
      if (emailError) newErrors.email = emailError;

      if (!formData.country) {
        newErrors.country = "Please select your country";
      }
    }

    if (step === 2) {
      // Security
      const passwordError = validatePassword(formData.password);
      if (passwordError) newErrors.password = passwordError;

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }

      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = "You must agree to the terms and conditions";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting form...");

    if (!validateStep(2)) {
      console.log("Step 2 validation failed.");
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors

    try {
      const result = await register(formData);

      if (result.success) {
        setToast({
          type: "success",
          message:
            result.data.message ||
            "Account created successfully! Redirecting to your dashboard.",
        });

        setTimeout(() => {
          console.log("Redirecting to /dashboard...");
          navigate("/dashboard");
        }, 2000);
      } else {
        // Handle specific error cases
        const errorMessage =
          result.error || "Registration failed. Please try again.";

        // Check for specific error types and set field errors
        if (
          errorMessage.toLowerCase().includes("email") &&
          errorMessage.toLowerCase().includes("exists")
        ) {
          setErrors({ email: "An account with this email already exists" });
          setCurrentStep(1); // Go back to step 1 to fix email
        } else if (errorMessage.toLowerCase().includes("email")) {
          setErrors({ email: errorMessage });
          setCurrentStep(1);
        } else if (errorMessage.toLowerCase().includes("password")) {
          setErrors({ password: errorMessage });
        } else if (
          errorMessage.toLowerCase().includes("first name") ||
          errorMessage.toLowerCase().includes("firstname")
        ) {
          setErrors({ firstName: errorMessage });
          setCurrentStep(1);
        } else if (
          errorMessage.toLowerCase().includes("last name") ||
          errorMessage.toLowerCase().includes("lastname")
        ) {
          setErrors({ lastName: errorMessage });
          setCurrentStep(1);
        } else if (errorMessage.toLowerCase().includes("country")) {
          setErrors({ country: errorMessage });
          setCurrentStep(1);
        } else {
          setToast({
            type: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      console.error("Caught error:", error);

      // Handle network errors
      if (!navigator.onLine) {
        setToast({
          type: "error",
          message:
            "No internet connection. Please check your network and try again.",
        });
      } else if (
        error.name === "TypeError" &&
        error.message.includes("fetch")
      ) {
        setToast({
          type: "error",
          message: "Unable to connect to server. Please try again later.",
        });
      } else {
        setToast({
          type: "error",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialSignUp = async (provider) => {
    try {
      // Implement social signup logic here
      setToast({
        type: "info",
        message: `${provider} signup coming soon!`,
      });
    } catch (error) {
      setToast({
        type: "error",
        message: `${provider} signup failed`,
      });
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        isDark
          ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
          : "bg-gradient-to-br from-blue-50 via-white to-blue-50"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card
          className={`backdrop-blur-sm ${
            isDark
              ? "bg-slate-800/50 border-slate-700/50"
              : "bg-white/80 border-gray-200/50"
          }`}
        >
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <span className="text-2xl font-bold text-white">T</span>
              </motion.div>
              <h1
                className={`text-2xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Create Account
              </h1>
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                Join thousands of successful traders
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-4">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    currentStep >= 1
                      ? "bg-blue-500 text-white"
                      : isDark
                      ? "bg-slate-600 text-slate-400"
                      : "bg-gray-300 text-gray-600"
                  }`}
                >
                  1
                </div>
                <div
                  className={`h-0.5 w-8 ${
                    currentStep >= 2
                      ? "bg-blue-500"
                      : isDark
                      ? "bg-slate-600"
                      : "bg-gray-300"
                  }`}
                />
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    currentStep >= 2
                      ? "bg-blue-500 text-white"
                      : isDark
                      ? "bg-slate-600 text-slate-400"
                      : "bg-gray-300 text-gray-600"
                  }`}
                >
                  2
                </div>
              </div>
            </div>

            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Social Sign Up Buttons */}
                <div className="space-y-3 mb-6">
                  <Button
                    variant="outline"
                    className={`w-full ${
                      isDark
                        ? "text-white border-slate-600 hover:bg-slate-700"
                        : "text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={() => handleSocialSignUp("Google")}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </Button>
                </div>

                {/* Divider */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div
                      className={`w-full border-t ${
                        isDark ? "border-slate-600" : "border-gray-300"
                      }`}
                    ></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span
                      className={`px-2 ${
                        isDark
                          ? "bg-slate-800 text-slate-400"
                          : "bg-white text-gray-500"
                      }`}
                    >
                      or
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      type="text"
                      name="firstName"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      error={errors.firstName}
                      className={
                        isDark
                          ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }
                    />
                    <Input
                      type="text"
                      name="lastName"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      error={errors.lastName}
                      className={
                        isDark
                          ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }
                    />
                  </div>

                  <Input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    className={
                      isDark
                        ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }
                  />

                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone number (optional)"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={
                      isDark
                        ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }
                  />

                  <div>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? "bg-slate-700/50 text-white placeholder-slate-400"
                          : "bg-white text-gray-900 placeholder-gray-500"
                      } ${
                        errors.country
                          ? "border-red-500"
                          : isDark
                          ? "border-slate-600"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select your country</option>
                      {countries.map((country) => (
                        <option
                          key={country}
                          value={country}
                          className={isDark ? "bg-slate-700" : "bg-white"}
                        >
                          {country}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <p className="mt-1 text-sm text-red-400">
                        {errors.country}
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleNext}
                  className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </motion.div>
            )}

            {/* Step 2: Security */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Create password"
                        value={formData.password}
                        onChange={handleInputChange}
                        error={errors.password}
                        className={`pr-10 ${
                          isDark
                            ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                          isDark
                            ? "text-slate-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {showPassword ? "🙈" : "👁️"}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div className="mt-2">
                        <div className="flex items-center space-x-2">
                          <div
                            className={`flex-1 rounded-full h-2 ${
                              isDark ? "bg-slate-600" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                              style={{ width: `${passwordStrength}%` }}
                            />
                          </div>
                          <span
                            className={`text-xs ${
                              isDark ? "text-slate-400" : "text-gray-500"
                            }`}
                          >
                            {getPasswordStrengthText()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      error={errors.confirmPassword}
                      className={`pr-10 ${
                        isDark
                          ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                        isDark
                          ? "text-slate-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {showConfirmPassword ? "🙈" : "👁️"}
                    </button>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleInputChange}
                        className={`mt-1 rounded text-blue-500 focus:ring-blue-500 focus:ring-offset-0 ${
                          isDark
                            ? "border-slate-600 bg-slate-700"
                            : "border-gray-300 bg-white"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          isDark ? "text-slate-300" : "text-gray-700"
                        }`}
                      >
                        I agree to the{" "}
                        <Link
                          to="/terms"
                          className={
                            isDark
                              ? "text-blue-400 hover:text-blue-300"
                              : "text-blue-600 hover:text-blue-500"
                          }
                        >
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/privacy"
                          className={
                            isDark
                              ? "text-blue-400 hover:text-blue-300"
                              : "text-blue-600 hover:text-blue-500"
                          }
                        >
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                    {errors.agreeToTerms && (
                      <p className="text-sm text-red-400">
                        {errors.agreeToTerms}
                      </p>
                    )}

                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="agreeToMarketing"
                        checked={formData.agreeToMarketing}
                        onChange={handleInputChange}
                        className={`mt-1 rounded text-blue-500 focus:ring-blue-500 focus:ring-offset-0 ${
                          isDark
                            ? "border-slate-600 bg-slate-700"
                            : "border-gray-300 bg-white"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          isDark ? "text-slate-300" : "text-gray-700"
                        }`}
                      >
                        I want to receive trading insights and market updates
                      </span>
                    </label>
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className={`flex-1 ${
                        isDark
                          ? "text-white border-slate-600 hover:bg-slate-700"
                          : "text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading || isSubmitting}
                    >
                      {loading || isSubmitting ? (
                        <div className="flex items-center justify-center space-x-2">
                          <LoadingSpinner size="sm" />
                          <span>Creating Account...</span>
                        </div>
                      ) : (
                        "Create Account"
                      )}
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Sign In Link */}
            <div className="mt-6 text-center">
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                Already have an account?{" "}
                <Link
                  to="/login"
                  className={`font-medium transition-colors ${
                    isDark
                      ? "text-blue-400 hover:text-blue-300"
                      : "text-blue-600 hover:text-blue-500"
                  }`}
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SignUp;
